import React, { useState, useEffect, memo } from 'react'
import omit from 'lodash/omit'
import find from 'lodash/find'
import moment from 'moment'

import GroupForm from './form'
import GroupTable from './table'
import SecurityAdministrationLayout from '../../commons/SecurityAdministrationLayout'

import { searchAction } from 'actions/SecurityAdminActions'
import { VALUES } from 'utils/constants'
import { buildFiltersDescriptors } from '../../commons/SearchFilters'
import { useApiRequest } from 'utils/customHooks/useApiRequest'
import { getFromApi } from 'actions'

const { FORM_BEHAVIOR, TABLE_DATA_INTIAL_STATE, PAGING_ORDER_INITIAL_STATE, STATUS_OPTIONS } = VALUES
const { REGISTER, SEARCH, EDIT } = FORM_BEHAVIOR

const TITLE = {
   [REGISTER]: 'Registrar Grupo',
   [SEARCH]: 'Consultar Grupo',
   [EDIT]: 'Editar Grupo'
}

const GROUP_FORM_INTIAL_STATE = {
   id: '',
   name: '',
   description: '',
   status: '',
   finalDate: null,
   domain: '',
   roles: []
}

const SEARCH_URL = `${process.env.REACT_APP_GROUPS_API}/groups/search`
const SEARCH_PAYLOAD_SCHEMA = ['name', 'status', 'finalDate', 'domain']

const options = STATUS_OPTIONS

const toAutocompleteMapper = (data, roles = []) => ({
   ...data,
   roles: data?.roles?.map(rol => find(roles, { id: rol?.id }))
})

function Group({ isSearch }) {
   const [openPopover, setOpenPopover] = useState(true)

   // NOTA: El estado del Form se mantiene aquí para que se preserven los datos si se cierra la popup que contiene al form
   const [formData, setFormData] = useState(GROUP_FORM_INTIAL_STATE)
   const [formBehavior, setFormBehavior] = useState(isSearch ? SEARCH : REGISTER)
   const [searchFilters, setSearchFilters] = useState({})
   const [tableData, setTableData] = useState(TABLE_DATA_INTIAL_STATE)
   const [paginationParams, setPaginationParams] = useState(PAGING_ORDER_INITIAL_STATE)
   const [roles, setRoles] = useState([])

   function cleanForm() {
      setFormData(GROUP_FORM_INTIAL_STATE)
      setFormBehavior(isSearch ? SEARCH : REGISTER)
   }

   function clean() {
      cleanForm()
      setSearchFilters({})
      setTableData(TABLE_DATA_INTIAL_STATE)
      setPaginationParams(PAGING_ORDER_INITIAL_STATE)
      setOpenPopover(true)
   }

   // ------------ search groups ------------
   async function _searchGroups(externalFilters, pagingFromTableParams) {
      await searchAction(
         SEARCH_URL,
         SEARCH_PAYLOAD_SCHEMA,
         cleanForm,
         setTableData,
         externalFilters,
         [searchFilters, setSearchFilters],
         pagingFromTableParams,
         [paginationParams, setPaginationParams]
      )
   }

   const searchGroups = useApiRequest(_searchGroups, 'Grupos: Consultar')

   function getFiltersDescriptors(filters) {
      const descriptors = buildFiltersDescriptors(options)(filters) || []
      return descriptors
   }

   async function removeSearchFilter(filter) {
      const remainingFilters = omit(searchFilters, [filter.filterName])
      await searchGroups(remainingFilters, PAGING_ORDER_INITIAL_STATE)
   }

   // Click en el lapiz de la tabla
   function onEdit(rowData) {
      setFormBehavior(EDIT)
      const data = {}
      for (const formField in GROUP_FORM_INTIAL_STATE) {
         if (rowData[formField]) {
            data[formField] = rowData[formField]
         }
      }
      if (rowData.finalDate) {
         data.finalDate = moment(rowData.finalDate, 'DD-MM-YYYY')
      }
      setFormData(toAutocompleteMapper(data, roles))
      setOpenPopover(true)
   }

   async function _getActiveRoles() {
      const url = `${process.env.REACT_APP_ROLES_API}/roles/active`
      const roles = await getFromApi(url)
      setRoles(roles)
   }

   const getActiveRoles = useApiRequest(_getActiveRoles, 'Roles: obtener roles Activos')

   useEffect(() => {
      clean()
   }, [isSearch]) // eslint-disable-line react-hooks/exhaustive-deps

   useEffect(() => {
      getActiveRoles()
   }, [])

   return (
      <SecurityAdministrationLayout
         title={TITLE[formBehavior]}
         form={
            <GroupForm
               cleanCallback={cleanForm}
               formState={[formData, setFormData]}
               roles={roles}
               formBehavior={formBehavior}
               setTableData={setTableData}
               searchCallback={searchGroups}
               setOpenPopover={setOpenPopover}
            />
         }
         table={
            <GroupTable
               order={paginationParams.order}
               groups={tableData}
               searchCallback={searchGroups}
               onEdit={onEdit}
               cleanCallback={clean}
               isSearch={isSearch}
            />
         }
         popoverProp={openPopover}
         setPopoverProp={setOpenPopover}
         searchFilters={getFiltersDescriptors(searchFilters)}
         onRemoveFilter={removeSearchFilter}
      />
   )
}

export default memo(Group)
